"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { isValidEmail } from "@/lib/emailCapture";

const USER_STORAGE_KEY = "vault88-user";
export const SAVE_FAVORITES_DISMISSED_KEY = "vault88-save-favorites-dismissed";

type Props = {
  open: boolean;
  // Called once the prompt is resolved (submitted or skipped) so the parent can
  // continue navigating away from the deck.
  onClose: () => void;
};

function cacheEmail(email: string) {
  try {
    const raw = window.localStorage.getItem(USER_STORAGE_KEY);
    const existing = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify({ ...existing, email }));
  } catch (error) {
    console.error("[SaveFavorites] Failed to cache email", error);
  }
}

function markDismissed() {
  try {
    window.localStorage.setItem(SAVE_FAVORITES_DISMISSED_KEY, "1");
  } catch (error) {
    console.error("[SaveFavorites] Failed to persist dismissal", error);
  }
}

export function SaveFavoritesModal({ open, onClose }: Props) {
  const [email, setEmail] = useState("");
  const [subscribe, setSubscribe] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const emailValid = isValidEmail(email);

  const skip = () => {
    markDismissed();
    onClose();
  };

  const submit = async () => {
    if (!emailValid || isSaving) return;
    const trimmed = email.trim();
    setIsSaving(true);
    try {
      await fetch("/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      if (subscribe) {
        await fetch("/api/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: trimmed }),
        });
      }
      cacheEmail(trimmed);
    } catch (error) {
      console.error("[SaveFavorites] Failed to save email", error);
    } finally {
      setIsSaving(false);
      markDismissed();
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="save-favorites-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-8"
          onClick={skip}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className="relative z-10 w-full max-w-md bg-white p-7 text-center shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close"
              className="absolute right-4 top-4 text-3xl font-light text-slate-500 transition hover:text-slate-900"
              onClick={skip}
            >
              &times;
            </button>
            <div className="space-y-5 pt-4">
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold text-slate-900">Save your favorites</h2>
                <p className="text-slate-600">
                  Enter your email to save the pieces you loved — and be the first to hear about new arrivals.
                </p>
              </div>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
              />
              <label className="flex cursor-pointer items-center justify-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={subscribe}
                  onChange={(event) => setSubscribe(event.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 accent-[#1b2b52]"
                />
                Subscribe to new arrivals &amp; updates
              </label>
              <button
                type="button"
                disabled={!emailValid || isSaving}
                onClick={submit}
                className={cn(
                  "w-full rounded-sm bg-royal px-6 py-3 text-sm font-semibold uppercase tracking-[0.4em] text-white transition hover:-translate-y-0.5",
                  (!emailValid || isSaving) && "cursor-not-allowed opacity-50 hover:translate-y-0"
                )}
              >
                {isSaving ? "Saving…" : "Save favorites"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
